/**
 * Typed testid registry for the `batchProcesses` domain — the SINGLE source of
 * truth for every `data-testid` this domain renders (TV1-2055, rolling out the
 * TV1-1866 pattern).
 *
 * Components spread these values into `data-testid`; any Playwright / jsdom spec
 * that needs a batch-progress selector imports the SAME object. A renamed key
 * changes the string in ONE place and both sides move together — instead of a
 * runtime "element not found" when a component testid drifts from a hardcoded
 * spec string (e2e is not run in CI, so that drift fails silently). The
 * `teselly/no-raw-testid` ESLint rule forbids raw `data-testid` literals in this
 * domain so new testids must land here.
 *
 * Naming convention: `batch-[section]-[element]` in kebab-case (see
 * `.claude/rules/e2e-testing.md`). Values are preserved verbatim from the
 * pre-existing literals (no rename) — the `batch-progress-*` prefix predates this
 * registry and is kept as-is so the migration is value-safe.
 */
export const batchProcessesTestIds = {
  // --- detail: credential revocation (TV1-2446 slice 6) ---
  /** Notice shown when Retry is disabled because the batch's channel credential is revoked. */
  detailRetryCredentialRevokedNotice: 'batch-detail-retry-credential-revoked-notice',
  /** Same notice, on one run's card in the active-run strip above the publications / inventory lists. */
  activeRegionRetryCredentialRevokedNotice: ( processId: string ): string => `batch-active-region-retry-credential-revoked-notice-${ processId }`,
  /** Same notice, on one process card of the batch-processes list (desktop card and phone row never co-render). */
  listRetryCredentialRevokedNotice: ( processId: string ): string => `batch-list-retry-credential-revoked-notice-${ processId }`,

  // --- detail: progress bar ---
  /** Segmented progress-bar container. */
  progressBar: 'batch-progress-bar',
  /** Successful-item count label. */
  progressSuccessCount: 'batch-progress-success-count',
  /** Errored-item count label. */
  progressErrorCount: 'batch-progress-error-count',
  /** "Finalizing…" status label. */
  progressFinalizing: 'batch-progress-finalizing',

  // --- detail: ERP item import (ERP-ONBOARDING slice 5) ---
  /** The per-reason breakdown block on an ERP catalog-import run. */
  erpImportBreakdown: 'batch-erp-import-breakdown',
  /**
   * One counter in that breakdown, keyed by reason
   * (`imported` | `unchanged` | `unresolved` | `errored`).
   */
  erpImportBreakdownCount: ( reason: string ): string =>
    `batch-erp-import-breakdown-${ reason }`,

  // --- detail: ERP stock import (ERP-ONBOARDING slice 9) ---
  /**
   * The COVERAGE line on an ERP stock sweep — "N of M ERP warehouses mapped". The
   * breakdown above is shared with the item import (same four buckets, same keys); this
   * is the one thing only a stock sweep has, because a partial Warehouse Mapping produces
   * a completely correct run over a fraction of the seller's depots, under a green bar.
   */
  erpStockImportCoverage: 'batch-erp-stock-import-coverage',

  // --- detail: ERP price import (ERP-ONBOARDING slice 10) ---
  /**
   * The COVERAGE line on an ERP price sweep — "N of M ERP price lists mapped". Its own
   * testid rather than a reuse of the stock one: the two renderers can appear in the same
   * run list, and a coverage assertion that cannot tell warehouses from price lists is a
   * test that passes on the wrong renderer.
   */
  erpPriceImportCoverage: 'batch-erp-price-import-coverage',

  // --- detail: primary actions (TV1-2294 ticket 48) ---
  /**
   * ONE id per action, shared by BOTH renderings of it: the desktop inline
   * `UnstyledButton` link inside the progress card, and the phone's docked
   * `BatchProcessDetailActionBar` button. The two branches are mutually
   * exclusive (`useIsMobile()`), so a single locator covers both surfaces and a
   * spec cannot assert one while the other silently loses the action — the
   * anti-drift shape ticket 42 established for the warehouse row.
   */
  detailAction: ( action: string ): string => `batch-detail-action-${ action }`,
  /** The docked action bar itself — present only below the `sm` breakpoint. */
  detailActionBar: 'batch-detail-action-bar',

  // --- list: phone rendering (TV1-2294 ticket 48) ---
  /**
   * The card list, keyed by section (`active` | `history`). The two sections
   * read from DIFFERENT stores (shared vs list) and can be empty independently,
   * so one id for both would make an assertion ambiguous.
   */
  mobileList: ( section: string ): string => `batch-mobile-list-${ section }`,
  /** One process card. SAME value at both densities — they never co-render. */
  mobileRow: ( processId: string ): string => `batch-mobile-row-${ processId }`,
  /** A section's empty state. */
  mobileEmpty: ( section: string ): string => `batch-mobile-empty-${ section }`,
  /** History pagination (narrowed: `size="sm"`, `siblings={0}`, no `withEdges`). */
  mobilePagination: 'batch-mobile-pagination',
  /**
   * A row action (`retry` | `cancel` | `archive`). Like `detailAction`, ONE id
   * serves the comfortable icon strip and the compact overflow-menu item,
   * because density picks exactly one of them. The desktop card's Retry icon
   * carries the `retry` id too (TV1-2446 slice 6) — desktop and phone never co-render.
   */
  listRowAction: ( action: string, processId: string ): string =>
    `batch-row-action-${ action }-${ processId }`
} as const;
